import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';

interface InactivityWarningModalProps {
    isOpen: boolean;
    remainingSeconds: number;
    onStayLoggedIn: () => void;
    onLogoutNow: () => void;
}

const InactivityWarningModal: React.FC<InactivityWarningModalProps> = ({
    isOpen,
    remainingSeconds,
    onStayLoggedIn,
    onLogoutNow
}) => {
    if (!isOpen) return null;

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-[9999]">
            <div className="bg-white rounded-lg max-w-md w-full shadow-2xl">
                {/* Header */}
                <div className="flex items-center space-x-3 p-6 border-b border-gray-200">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                        <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Inactivity Warning</h3>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-50 rounded-full mb-4">
                            <Clock className="w-10 h-10 text-yellow-600" />
                        </div>
                        <p className="text-gray-700 mb-2">
                            You will be logged out in
                        </p>
                        <div className="text-4xl font-bold text-yellow-600 mb-2">
                            {minutes > 0 && `${minutes}:`}
                            {seconds.toString().padStart(2, '0')}
                        </div>
                        <p className="text-sm text-gray-500">
                            {minutes > 0 ? 'minutes' : 'seconds'}
                        </p>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                        <p className="text-sm text-yellow-800">
                            You've been inactive for a while. For your security, we'll automatically log you out soon.
                        </p>
                    </div>

                    <p className="text-sm text-gray-600 text-center">
                        Click "Stay Logged In" to continue your session, or "Logout Now" to end it immediately.
                    </p>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
                    <button
                        type="button"
                        onClick={onLogoutNow}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                    >
                        Logout Now
                    </button>
                    <button
                        type="button"
                        onClick={onStayLoggedIn}
                        className="px-6 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                        style={{
                            backgroundColor: '#69BE28',
                            boxShadow: '0 2px 4px rgba(105, 190, 40, 0.2)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#5AAA23';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#69BE28';
                        }}
                    >
                        Stay Logged In
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InactivityWarningModal;
